import type { BoxProps } from '@hover/blueprint';
import { Box, useSize } from '@hover/blueprint';
import type { RefObject } from 'react';
import React, { forwardRef, useEffect, useMemo } from 'react';

import { GeneratedImageThumbnail } from 'src/modules/homeownerApp/components/GeneratedImageThumbnail';
import { PROJECT_DETAILS_PADDING } from 'src/modules/homeownerApp/components/Projects/Project/ProjectCardSection';
import {
  useFilterToCreatedFromOriginalOrStyle,
  useGenerateInitialBatch,
  useRandomOrder,
  useScrollableLeftAndRight,
} from 'src/modules/homeownerApp/hooks';
import { ALL_STYLES } from 'src/modules/homeownerApp/static_values';
import {
  type AiOrSampleDisplayImage,
  type AiOrSampleOriginalImage,
  getImageId,
} from 'src/modules/homeownerApp/types';
import type { AiDisplayImage } from 'src/shared/graphql/generated';
import { useLocation } from 'src/shared/hooks';
import { Route, generatePath } from 'src/shared/route';
import { hideScrollbarsStyles } from 'src/shared/utils/styles';

import { useIdeas } from './useIdeas';

interface GeneratedImageContainerProps extends BoxProps {
  projectId: string;
  images: AiOrSampleDisplayImage[];
  originalImage: AiOrSampleOriginalImage;
}

export type InViewItem = {
  id: number;
  isInView: boolean;
};

export const GeneratedImageContainer = forwardRef(
  (
    {
      projectId,
      images,
      originalImage,
      ...props
    }: GeneratedImageContainerProps,
    refObj,
  ) => {
    const ref = refObj as RefObject<HTMLDivElement>;
    const size = useSize(ref);
    const location = useLocation();
    const { setCanScrollLeft, setCanScrollRight, setScroll } = useIdeas();
    const { canScrollLeft, canScrollRight, scroll } = useScrollableLeftAndRight(
      {
        ref,
        scrollAmount: (size?.width ?? 0) / 2,
      },
    );

    useEffect(() => {
      setCanScrollLeft(canScrollLeft);
      setCanScrollRight(canScrollRight);
      setScroll(() => scroll);
    }, [
      canScrollLeft,
      canScrollRight,
      scroll,
      setCanScrollLeft,
      setCanScrollRight,
      setScroll,
    ]);

    useGenerateInitialBatch(originalImage, ALL_STYLES, 4);
    const filteredDisplayImages = useFilterToCreatedFromOriginalOrStyle(
      images,
      true,
    );
    const randomizedDisplayImages = useRandomOrder(filteredDisplayImages);
    const latestBatchOfImages = useMemo(
      () => randomizedDisplayImages.slice(-4),
      [randomizedDisplayImages],
    );

    return (
      <Box
        gap={{ base: 200, tablet: 400 }}
        mt="0 !important"
        overflowX="auto"
        paddingX={PROJECT_DETAILS_PADDING}
        ref={ref}
        scrollBehavior="smooth"
        scrollSnapAlign="start"
        scrollSnapType="x mandatory"
        sx={hideScrollbarsStyles()}
        {...props}
      >
        {latestBatchOfImages.map(image => (
          <GeneratedImageThumbnail
            canEnterFullscreen
            enableLayoutAnimation={false}
            flexShrink={{ base: 0, desktop: 'unset' }}
            hrefOverride={generatePath(
              Route.HOMEOWNER_APP_PROJECT_DISPLAY_FULLSCREEN.path,
              {
                aiDisplayImageId: (image as AiDisplayImage).id,
                projectId,
              },
            )}
            image={image}
            key={getImageId(image)}
            marginInlineStart="0 !important"
            maxWidth={{
              base: 'calc(100vw - ((var(--hover-space-500) * 2) + var(--hover-space-200)))',
              width250:
                'calc((100vw - (var(--hover-space-600) * 2) - (var(--hover-space-500) + var(--hover-space-200))) / 2)',
              desktop: 'unset',
            }}
            originalImage={originalImage}
            referrer={location.pathname}
            scrollSnapAlign={{ base: 'center', tablet: 'none' }}
            scrollSnapStop="always"
          />
        ))}
      </Box>
    );
  },
);
